import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { TextField, Button, CircularProgress } from "@material-ui/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import { faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons";

import type { IPartner } from "App/Modules/Models";
import type { IPartnersRepository } from "App/Modules/Repositories";
import "./_data-policy.scss";

type DataPolicyDialogProps = {
    onClose: () => void;
    partnersRepository: IPartnersRepository;
    partner: IPartner;
    onDataPolicyChange: () => void;
};

export const DataPolicyDialog = (props: DataPolicyDialogProps) => {
    const { t } = useTranslation();
    const { onClose, partnersRepository, partner } = props;
    const [saveError, setSaveError] = useState(false);
    const [isButtonDisabled, setIsButtonDisabled] = useState(true);
    const [isChangesSaved, setIsChangesSaved] = useState(false);
    const [maxDaysRecordedVideo, setMaxDaysRecordedVideo] = useState(partner.dataRetention.maxDaysRecordedVideo);

    const handleClose = () => {
        onClose();
    };

    const handleSave = (event: React.MouseEvent) => {
        event.preventDefault();

        // add current data policy
        if (maxDaysRecordedVideo === partner.dataRetention.maxDaysRecordedVideo) {
            setIsButtonDisabled(true);
            setIsChangesSaved(true);
            partnersRepository.addDataRetention(partner.id, partner.dataRetention).subscribe(
                (response) => {
                    if (!response.hasError && response.data) {
                        props.onDataPolicyChange();
                        onClose();
                    }
                },
                () => {
                    setSaveError(true);
                },
            );
        }

        const editedDataPolicy = {
            id: partner.dataRetention.id,
            maxDaysRecordedVideo,
        };

        // edit data policy
        if (maxDaysRecordedVideo !== partner.dataRetention.maxDaysRecordedVideo) {
            setIsButtonDisabled(true);
            setIsChangesSaved(true);
            partnersRepository.patchDataRetention(partner.id, partner.dataRetention.id, editedDataPolicy).subscribe(
                (response) => {
                    if (!response.hasError && response.data) {
                        props.onDataPolicyChange();
                        onClose();
                    }
                },
                () => {
                    setSaveError(true);
                },
            );
        }
    };

    return (
        <div className="dialog card data-policy">
            <div className="dialog__section dialog__section--full">
                <h1 className="title title--h1">{t("Entities.DataRetentions.DaysToKeep")}</h1>
                <span className="data-policy__info">
                    <FontAwesomeIcon icon={faInfoCircle} fixedWidth className="state-tag__icon" />
                    <p className="paragraph paragraph--small paragraph--no-spacing">
                        {t("Gui.Manage.EditDataRetentionHelpText")}
                    </p>
                </span>
                <span className="data-policy__info">
                    <p className="paragraph paragraph--small paragraph--no-spacing">
                        {t("Gui.Manage.DataPolicy.CloudDataInformation")}
                    </p>
                </span>
            </div>
            <div className="dialog__section dialog__section--full">
                <form className="form">
                    <div className="form__row">
                        <p className="caption">{t("Entities.DataRetentions.RecordedVideo")}</p>
                        <TextField
                            fullWidth
                            variant="outlined"
                            size="small"
                            type="number"
                            value={maxDaysRecordedVideo}
                            onChange={(e) => {
                                setMaxDaysRecordedVideo(parseInt(e.target.value, 10));
                                setIsButtonDisabled(false);
                            }}
                        />
                    </div>
                </form>
            </div>
            {saveError ? (
                <div className="dialog__section dialog__section--full">
                    <div className="notice notice--critical">
                        <FontAwesomeIcon className="notice__icon" fixedWidth icon={faExclamationTriangle} />
                        <p className="caption">{t("Error.ErrorSaving")}</p>
                    </div>
                </div>
            ) : null}
            <div className="dialog__section--buttons">
                <Button onClick={handleClose}>{t("Gui.Buttons.Cancel")}</Button>
                <Button
                    variant="contained"
                    color="primary"
                    disabled={isButtonDisabled}
                    startIcon={isChangesSaved ? <CircularProgress size={20} color="inherit" /> : null}
                    onClick={(e: React.MouseEvent) => handleSave(e)}
                >
                    {t("Gui.Buttons.Save")}
                </Button>
            </div>
        </div>
    );
};
